import { formatDateRu } from "../../../utils/appointmentLabels";
import { ageFromDob } from "../../../utils/clientAppUtils";

export const LEVEL_SYSTEMS = [
  { id: "", label: "Не указывать" },
  { id: "gypdan", label: "Гып и дан" },
  { id: "kyudan", label: "Кю и дан" },
  { id: "custom", label: "Своё название" },
];

function beltOptions(pupilKind, pupilWord) {
  return [
    ...Array.from({ length: 10 }, (_, index) => {
      const grade = 10 - index;
      return {
        value: `${pupilKind}:${grade}`,
        kind: pupilKind,
        grade,
        label: `${grade} ${pupilWord}`,
      };
    }),
    ...Array.from({ length: 9 }, (_, index) => {
      const grade = index + 1;
      return { value: `dan:${grade}`, kind: "dan", grade, label: `${grade} дан` };
    }),
  ];
}

export const GYP_DAN_OPTIONS = beltOptions("gyp", "гып");
export const KYU_DAN_OPTIONS = beltOptions("kyu", "кю");

export function levelSystemOf(edit) {
  const system = edit?.skill_level_system;
  if (system === "gypdan" || system === "kyudan" || system === "custom") return system;
  if (edit?.skill_level_kind === "kyu") return "kyudan";
  if (edit?.skill_level_kind === "gyp" || edit?.skill_level_kind === "dan") return "gypdan";
  if (String(edit?.skill_level_label || "").trim()) return "custom";
  return "";
}

export function levelOptionsFor(system) {
  if (system === "kyudan") return KYU_DAN_OPTIONS;
  if (system === "gypdan") return GYP_DAN_OPTIONS;
  return [];
}

export const SPORT_RANK_OPTIONS = [
  { value: "3j", label: "3 юношеский" },
  { value: "2j", label: "2 юношеский" },
  { value: "1j", label: "1 юношеский" },
  { value: "3", label: "3 разряд" },
  { value: "2", label: "2 разряд" },
  { value: "1", label: "1 разряд" },
  { value: "kms", label: "КМС" },
  { value: "ms", label: "МС" },
  { value: "msmk", label: "МСМК" },
  { value: "zms", label: "ЗМС" },
];

export function skillLevelValue(kind, grade) {
  if (kind !== "gyp" && kind !== "kyu" && kind !== "dan") return "";
  if (grade === "" || grade == null) return "";
  return `${kind}:${Number(grade)}`;
}

export function parseSkillLevelValue(value) {
  const [kind, raw] = String(value || "").split(":");
  if (kind !== "gyp" && kind !== "kyu" && kind !== "dan") {
    return { skill_level_kind: "", skill_level: "" };
  }
  const grade = Number(raw);
  if (!Number.isInteger(grade)) {
    return { skill_level_kind: "", skill_level: "" };
  }
  return { skill_level_kind: kind, skill_level: grade };
}

export function skillLevelLabel(kind, grade, label) {
  const custom = String(label || "").trim();
  if (kind === "custom" || (!kind && custom)) return custom;
  const option = [...GYP_DAN_OPTIONS, ...KYU_DAN_OPTIONS].find(
    (item) => item.kind === kind && item.grade === Number(grade),
  );
  return option?.label || custom;
}

export function sportRankLabel(value) {
  if (!value) return "";
  return SPORT_RANK_OPTIONS.find((item) => item.value === value)?.label || String(value);
}

export function levelPreview(edit) {
  return (
    skillLevelLabel(edit?.skill_level_kind, edit?.skill_level, edit?.skill_level_label) ||
    "не указан"
  );
}

export function rankPreview(edit) {
  const label = sportRankLabel(edit?.sport_rank);
  if (!label) return "не указан";
  if (dateTone(edit?.sport_rank_expires_at) === "expired") return `${label} · просрочен`;
  return label;
}

export function dateTone(iso, soonDays = 30) {
  if (!iso) return "empty";
  const time = new Date(`${String(iso).slice(0, 10)}T12:00:00`).getTime();
  if (Number.isNaN(time)) return "empty";
  const days = Math.ceil((time - Date.now()) / 86400000);
  if (days < 0) return "expired";
  if (days <= soonDays) return "soon";
  return "ok";
}

export function formatCardDate(iso, options) {
  if (!iso) return "";
  return formatDateRu(
    `${String(iso).slice(0, 10)}T12:00:00`,
    options || { day: "numeric", month: "long", year: "numeric" },
  );
}

export function formatShortDate(iso) {
  return formatCardDate(iso, { day: "numeric", month: "short", year: "numeric" });
}

export function ageLabel(age) {
  const n = Number(age);
  if (!Number.isFinite(n) || n < 0) return "";
  const mod10 = n % 10;
  const mod100 = n % 100;
  let word = "лет";
  if (mod10 === 1 && mod100 !== 11) word = "год";
  else if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) word = "года";
  return `${n} ${word}`;
}

export function birthDateLine(dob) {
  if (!dob) return "Дата рождения не указана";
  const age = ageFromDob(dob);
  const date = formatCardDate(dob);
  return age == null ? date : `${date} · ${ageLabel(age)}`;
}

export function genderShort(value) {
  if (value === "male") return "М";
  if (value === "female") return "Ж";
  if (value === "other") return "Другое";
  return "—";
}

export function medicalClearancePreview(edit) {
  if (!edit?.medical_clearance) return "нет";
  if (!edit.medical_clearance_expires_at) return "без срока";
  const tone = dateTone(edit.medical_clearance_expires_at, 14);
  const until = formatShortDate(edit.medical_clearance_expires_at);
  if (tone === "expired") return "просрочен";
  return `до ${until}`;
}

export function medicalClearanceHint(edit) {
  if (!edit?.medical_clearance) return "нет допуска";
  if (!edit.medical_clearance_expires_at) return "укажите срок";
  const tone = dateTone(edit.medical_clearance_expires_at, 14);
  if (tone === "expired") return "нужно обновить";
  if (tone === "soon") return "истекает скоро";
  return "действует";
}

export function rankFormError(edit) {
  if (!edit?.sport_rank) return "";
  if (!edit.sport_rank_order_date) return "Укажите дату приказа";
  if (!edit.sport_rank_expires_at) return "Укажите срок окончания разряда";
  return "";
}

export function medicalFormError(edit) {
  if (edit?.medical_clearance && !edit.medical_clearance_expires_at) {
    return "Укажите дату окончания допуска";
  }
  return "";
}
